import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../services/context';
import PageHeader from '../../Components/PageHeader';
import SubscriptionClasses from './SubscriptionClasses';
import request from '../../services/request';
import endPoint from '../../services/endPoint';

/**
 * Interface complète de gestion des abonnements
 * Basée sur SubscriptionController Laravel avec toutes les fonctionnalités
 */
const AdvancedSubscriptions = () => {
  const { user } = useContext(AppContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtres
  const [filters, setFilters] = useState({
    status: '',
    user_id: '',
    plan_id: '',
    search: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showClassesModal, setShowClassesModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);

  // Formulaire de création
  const [createForm, setCreateForm] = useState({
    user_id: '',
    plan_slug: '',
    start_date: '',
    trial_days: 0,
    payment_method: 'orange_money',
    payment_reference: '',
    auto_renew: true,
    metadata: {}
  });

  // Formulaire d'annulation
  const [cancelForm, setCancelForm] = useState({
    reason: '',
    immediate: false
  });

  useEffect(() => {
    loadInitialData();
  }, [currentPage]);

  useEffect(() => {
    applyFilters();
  }, [filters, subscriptions]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [subscriptionsRes, plansRes, usersRes, studentsRes] = await Promise.all([
        request.get(`${endPoint.subscriptions}?page=${currentPage}`),
        request.get(endPoint.subscriptionPlans),
        request.get(`${endPoint.utilisateurs}?profilep=parent`), // Parents qui peuvent avoir des abonnements
        request.get(`${endPoint.utilisateurs}?profilep=eleve`) // Étudiants
      ]);

      const subsData = subscriptionsRes.data?.data || [];
      setSubscriptions(Array.isArray(subsData.data) ? subsData.data : []);
      setTotalPages(subsData.last_page || 1);

      setSubscriptionPlans(Array.isArray(plansRes.data?.data) ? plansRes.data.data : []);
      setUsers(Array.isArray(usersRes.data?.data) ? usersRes.data.data : []);
      setStudents(Array.isArray(studentsRes.data?.data) ? studentsRes.data.data : []);

      toast.success('Données chargées avec succès');
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des données');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...subscriptions];

    if (filters.status) {
      filtered = filtered.filter(sub => sub.status === filters.status);
    }

    if (filters.user_id) {
      filtered = filtered.filter(sub => sub.user_id == filters.user_id);
    }

    if (filters.search) {
      filtered = filtered.filter(sub =>
        sub.slug?.toLowerCase().includes(filters.search.toLowerCase()) ||
        sub.user?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        sub.subscription_plan?.name?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredSubscriptions(filtered);
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    try {
      const response = await request.post(endPoint.subscriptions, createForm);
      if (response.data?.success) {
        toast.success('Abonnement créé avec succès');
        setShowCreateModal(false);
        resetCreateForm();
        loadInitialData();
      }
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const handleShowDetails = async (subscription) => {
    try {
      const response = await request.get(`${endPoint.subscriptions}/${subscription.slug}`);
      if (response.data?.success) {
        const subscriptionData = response.data.data.subscription;
        
        // Si les classes ne sont pas incluses dans la réponse, les récupérer séparément
        if (!subscriptionData.classes) {
          try {
            const classesResponse = await request.get(`${endPoint.subscriptions}/${subscription.slug}/classes`);
            if (classesResponse.data?.success) {
              subscriptionData.classes = classesResponse.data.data;
            }
          } catch (classesError) {
            console.warn('Impossible de récupérer les classes:', classesError);
            subscriptionData.classes = [];
          }
        }
        console.log('Détails de l\'abonnement:', subscriptionData);
        setSelectedSubscription(subscriptionData);
        setSubscriptionStats(response.data.data.stats);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Erreur détails:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleAddStudent = async (studentId) => {
    try {
      const response = await request.post(
        `${endPoint.subscriptions}/${selectedSubscription.slug}/students`,
        { student_id: studentId }
      );
      if (response.data?.success) {
        toast.success('Élève ajouté avec succès');
        handleShowDetails(selectedSubscription); // Rafraîchir les détails
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet élève ?')) return;

    try {
      const response = await request.delete(
        `${endPoint.subscriptions}/${selectedSubscription.slug}/students/${studentId}`
      );
      if (response.data?.success) {
        toast.success('Élève retiré avec succès');
        handleShowDetails(selectedSubscription); // Rafraîchir les détails
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleRemoveClass = async (classSlug) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cette classe ?')) return;

    try {
      const response = await request.delete(
        `${endPoint.subscriptions}/${selectedSubscription.slug}/classes/${classSlug}`
      );
      if (response.data?.success) {
        toast.success('Classe retirée avec succès');
        handleShowDetails(selectedSubscription); // Rafraîchir les détails
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleRenewSubscription = async () => {
    try {
      const response = await request.post(`${endPoint.subscriptions}/${selectedSubscription.slug}/renew`);
      if (response.data?.success) {
        toast.success('Abonnement renouvelé avec succès');
        setShowRenewModal(false);
        loadInitialData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du renouvellement');
    }
  };

  const handleCancelSubscription = async (e) => {
    e.preventDefault();
    try {
      const response = await request.post(
        `${endPoint.subscriptions}/${selectedSubscription.slug}/cancel`,
        cancelForm
      );
      if (response.data?.success) {
        toast.success('Abonnement annulé avec succès');
        setShowCancelModal(false);
        setCancelForm({ reason: '', immediate: false });
        loadInitialData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      user_id: '',
      plan_slug: '',
      start_date: '',
      trial_days: 0,
      payment_method: 'orange_money',
      payment_reference: '',
      auto_renew: true,
      metadata: {}
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-success',
      trial: 'bg-info',
      expired: 'bg-warning',
      cancelled: 'bg-danger',
      suspended: 'bg-secondary'
    };

    const statusLabels = {
      active: 'Actif',
      trial: 'Essai',
      expired: 'Expiré',
      cancelled: 'Annulé',
      suspended: 'Suspendu'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatPrice = (price, currency = 'XOF') => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency;
  };

  const getRemainingDays = (endsAt) => {
    if (!endsAt) return null;
    const now = new Date();
    const endDate = new Date(endsAt);
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <PageHeader title="Gestion Avancée des Abonnements" />

      {/* Filtres et Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="active">Actif</option>
                    <option value="trial">Essai</option>
                    <option value="expired">Expiré</option>
                    <option value="cancelled">Annulé</option>
                    <option value="suspended">Suspendu</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Utilisateur</label>
                  <select
                    className="form-select"
                    value={filters.user_id}
                    onChange={(e) => setFilters({...filters, user_id: e.target.value})}
                  >
                    <option value="">Tous les utilisateurs</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.nom + ' ' + u.prenom}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Recherche</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher par slug, nom..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                  />
                </div>

                <div className="col-md-2">
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={() => setFilters({ status: '', user_id: '', plan_id: '', search: '' })}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Réinitialiser
                  </button>
                </div>

                <div className="col-md-2">
                  {user.permissions?.includes('create subscriptions') && (
                    <button
                      className="btn btn-primary w-100"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Nouvel Abonnement
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques Rapides */}
      <div className="row mb-4">
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-primary">{subscriptions.length}</h4>
              <small>Total</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-success">
                {subscriptions.filter(s => s.status === 'active').length}
              </h4>
              <small>Actifs</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-info">
                {subscriptions.filter(s => s.status === 'trial').length}
              </h4>
              <small>Essais</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-warning">
                {subscriptions.filter(s => s.status === 'expired').length}
              </h4>
              <small>Expirés</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-danger">
                {subscriptions.filter(s => s.status === 'cancelled').length}
              </h4>
              <small>Annulés</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-muted">
                {subscriptions.reduce((sum, s) => sum + (s.students_count || 0), 0)}
              </h4>
              <small>Total Élèves</small>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des Abonnements */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                Abonnements ({filteredSubscriptions.length})
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Abonnement</th>
                      <th>Utilisateur</th>
                      <th>Plan</th>
                      <th>Statut</th>
                      <th>Période</th>
                      <th>Élèves</th>
                      <th>Montant</th>
                      <th>Expire dans</th>
                      <th>Auto-renouvellement</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscriptions.map(subscription => {
                      const remainingDays = getRemainingDays(subscription.ends_at);
                      return (
                        <tr key={subscription.id}>
                          <td>
                            <div>
                              <strong>{subscription.slug}</strong>
                              <br />
                              <small className="text-muted">
                                ID: {subscription.id}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{subscription.user?.nom +" "+ subscription.user?.prenom || 'Utilisateur inconnu'}</strong>
                              <br />
                              <small className="text-muted">
                                {subscription.user?.email}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{subscription.subscription_plan?.name || 'Plan inconnu'}</strong>
                              <br />
                              <small className="text-muted">
                                {subscription.subscription_plan?.billing_cycle}
                              </small>
                            </div>
                          </td>
                          <td>{getStatusBadge(subscription.status)}</td>
                          <td>
                            <div>
                              <small>
                                <strong>Début:</strong> {formatDate(subscription.starts_at)}
                              </small>
                              <br />
                              <small>
                                <strong>Fin:</strong> {formatDate(subscription.ends_at)}
                              </small>
                              {subscription.trial_ends_at && (
                                <>
                                  <br />
                                  <small className="text-info">
                                    <strong>Fin essai:</strong> {formatDate(subscription.trial_ends_at)}
                                  </small>
                                </>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {subscription.students_count || 0}
                            </span>
                          </td>
                          <td>
                            <strong>{formatPrice(subscription.amount_paid, subscription.currency)}</strong>
                          </td>
                          <td>
                            {remainingDays !== null ? (
                              <span className={`badge ${remainingDays <= 7 ? 'bg-warning' : remainingDays <= 0 ? 'bg-danger' : 'bg-success'}`}>
                                {remainingDays > 0 ? `${remainingDays} jours` : 'Expiré'}
                              </span>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${subscription.auto_renew ? 'bg-success' : 'bg-secondary'}`}>
                              {subscription.auto_renew ? 'Oui' : 'Non'}
                            </span>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-info"
                                onClick={() => handleShowDetails(subscription)}
                                title="Voir détails"
                              >
                                <i className="bi bi-eye"></i>
                              </button>

                              <button
                                className="btn btn-outline-success"
                                onClick={() => {
                                  setSelectedSubscription(subscription);
                                  setShowClassesModal(true);
                                }}
                                title="Gérer les classes"
                              >
                                <i className="bi bi-collection-play"></i>
                              </button>

                              {user.permissions?.includes('update subscriptions') && (
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => {
                                    setSelectedSubscription(subscription);
                                    setShowRenewModal(true);
                                  }}
                                  title="Renouveler"
                                  disabled={subscription.status === 'cancelled'}
                                >
                                  <i className="bi bi-arrow-repeat"></i>
                                </button>
                              )}

                              {user.permissions?.includes('cancel subscriptions') && (
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => {
                                    setSelectedSubscription(subscription);
                                    setShowCancelModal(true);
                                  }}
                                  title="Annuler"
                                  disabled={['cancelled', 'expired'].includes(subscription.status)}
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredSubscriptions.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox display-1 text-muted"></i>
                    <p className="text-muted mt-2">Aucun abonnement trouvé</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <nav>
                  <ul className="pagination mb-0 justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Précédent
                      </button>
                    </li>
                    
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Création */}
      {showCreateModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Créer un Nouvel Abonnement</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleCreateSubscription}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Utilisateur *</label>
                        <select
                          className="form-select"
                          value={createForm.user_id}
                          onChange={(e) => setCreateForm({...createForm, user_id: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner un utilisateur</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>{u.nom + " " + u.prenom} ({u.email})</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Plan d'Abonnement *</label>
                        <select
                          className="form-select"
                          value={createForm.plan_slug}
                          onChange={(e) => setCreateForm({...createForm, plan_slug: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner un plan</option>
                          {subscriptionPlans.map(plan => (
                            <option key={plan.id} value={plan.slug}>
                              {plan.name} - {formatPrice(plan.price, plan.currency)}/{plan.billing_cycle}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Date de Début</label>
                        <input
                          type="date"
                          className="form-control"
                          value={createForm.start_date}
                          onChange={(e) => setCreateForm({...createForm, start_date: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Jours d'Essai</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="30"
                          value={createForm.trial_days}
                          onChange={(e) => setCreateForm({...createForm, trial_days: parseInt(e.target.value) || 0})}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Méthode de Paiement</label>
                        <select
                          className="form-select"
                          value={createForm.payment_method}
                          onChange={(e) => setCreateForm({...createForm, payment_method: e.target.value})}
                        >
                          <option value="orange_money">Orange Money</option>
                          <option value="moov_money">Moov Money</option>
                          <option value="mtn_money">MTN Mobile Money</option>
                          <option value="card">Carte Bancaire</option>
                          <option value="bank_transfer">Virement Bancaire</option>
                          <option value="cash">Espèces</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Référence de Paiement</label>
                        <input
                          type="text"
                          className="form-control"
                          value={createForm.payment_reference}
                          onChange={(e) => setCreateForm({...createForm, payment_reference: e.target.value})}
                          placeholder="Ex: TXN123456"
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="autoRenew"
                          checked={createForm.auto_renew}
                          onChange={(e) => setCreateForm({...createForm, auto_renew: e.target.checked})}
                        />
                        <label className="form-check-label" htmlFor="autoRenew">
                          Renouvellement automatique
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetCreateForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Créer l'Abonnement
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal des Détails */}
      {showDetailsModal && selectedSubscription && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Détails de l'Abonnement {selectedSubscription.slug}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  {/* Informations Générales */}
                  <div className="col-md-6">
                    <h6 className="text-primary">Informations Générales</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th>Statut:</th>
                          <td>{getStatusBadge(selectedSubscription.status)}</td>
                        </tr>
                        <tr>
                          <th>Utilisateur:</th>
                          <td>{selectedSubscription.user?.nom + " " + selectedSubscription.user?.prenom}</td>
                        </tr>
                        <tr>
                          <th>Plan:</th>
                          <td>{selectedSubscription.subscription_plan?.name}</td>
                        </tr>
                        <tr>
                          <th>Période:</th>
                          <td>
                            {formatDate(selectedSubscription.starts_at)} - {formatDate(selectedSubscription.ends_at)}
                          </td>
                        </tr>
                        <tr>
                          <th>Montant:</th>
                          <td>{formatPrice(selectedSubscription.amount_paid, selectedSubscription.currency)}</td>
                        </tr>
                        <tr>
                          <th>Auto-renouvellement:</th>
                          <td>
                            <span className={`badge ${selectedSubscription.auto_renew ? 'bg-success' : 'bg-secondary'}`}>
                              {selectedSubscription.auto_renew ? 'Activé' : 'Désactivé'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Statistiques */}
                  <div className="col-md-6">
                    <h6 className="text-primary">Statistiques</h6>
                    {subscriptionStats && (
                      <div className="row">
                        <div className="col-6">
                          <div className="card text-center">
                            <div className="card-body py-2">
                              <h5 className="text-info mb-0">{subscriptionStats.students_count || 0}</h5>
                              <small>Élèves</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="card text-center">
                            <div className="card-body py-2">
                              <h5 className="text-success mb-0">{subscriptionStats.days_remaining || 0}</h5>
                              <small>Jours restants</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Gestion des Élèves */}
                  <div className="col-12 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-primary mb-0">Élèves Inscrits ({selectedSubscription.students?.length || 0})</h6>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowStudentsModal(true)}
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Ajouter un élève
                      </button>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Nom Prénom</th>
                            <th>Email</th>
                            <th>Date d'ajout</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSubscription.students && selectedSubscription.students.length > 0 ? (
                            selectedSubscription.students.map(subscriptionStudent => (
                              <tr key={subscriptionStudent.id}>
                                <td>{subscriptionStudent.student?.nom + " " + subscriptionStudent.student?.prenom}</td>
                                <td>{subscriptionStudent.student?.email}</td>
                                <td>{formatDate(subscriptionStudent.created_at)}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveStudent(subscriptionStudent.student?.id)}
                                  >
                                    <i className="bi bi-person-dash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center text-muted">
                                Aucun élève inscrit
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Gestion des Classes */}
                  <div className="col-12 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-primary mb-0">Classes Associées ({selectedSubscription.classes?.length || 0})</h6>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => setShowClassesModal(true)}
                      >
                        <i className="bi bi-collection-play me-1"></i>
                        Gérer les classes
                      </button>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Classe</th>
                            <th>Statut</th>
                            <th>Élèves actifs</th>
                            <th>Ajoutée le</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSubscription.classes?.length > 0 ? (
                            selectedSubscription.classes.map((subscriptionClass) => (
                              <tr key={subscriptionClass.slug}>
                              
                                <td>{subscriptionClass.classe?.label || `Classe ${subscriptionClass.classe_id}`}</td>
                                <td>
                                  <span className={`badge ${
                                    subscriptionClass.status === 'active' ? 'bg-success' :
                                    subscriptionClass.status === 'suspended' ? 'bg-warning' : 'bg-secondary'
                                  }`}>
                                    {subscriptionClass.status === 'active' ? 'Actif' :
                                     subscriptionClass.status === 'suspended' ? 'Suspendu' : 'Inactif'}
                                  </span>
                                </td>
                                <td>{subscriptionClass.active_students_count || 0}</td>
                                <td>{formatDate(subscriptionClass.added_at)}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveClass(subscriptionClass.slug)}
                                  >
                                    <i className="bi bi-collection-fill"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                Aucune classe associée
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter Élève */}
      {showStudentsModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ajouter un Élève</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStudentsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Sélectionner un élève</label>
                  <select className="form-select" id="studentSelect">
                    <option value="">Choisir un élève...</option>
                    {students.filter(student => 
                      !selectedSubscription?.students?.some(ss => ss.student?.id === student.id)
                    ).map(student => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStudentsModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const select = document.getElementById('studentSelect');
                    const studentId = select.value;
                    if (studentId) {
                      handleAddStudent(studentId);
                      setShowStudentsModal(false);
                    }
                  }}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Renouvellement */}
      {showRenewModal && selectedSubscription && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Renouveler l'Abonnement</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRenewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Êtes-vous sûr de vouloir renouveler l'abonnement <strong>{selectedSubscription.slug}</strong> ?</p>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  L'abonnement sera prolongé selon les paramètres du plan actuel.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRenewModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleRenewSubscription}
                >
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Renouveler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'Annulation */}
      {showCancelModal && selectedSubscription && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Annuler l'Abonnement</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelForm({ reason: '', immediate: false });
                  }}
                ></button>
              </div>
              <form onSubmit={handleCancelSubscription}>
                <div className="modal-body">
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Vous êtes sur le point d'annuler l'abonnement <strong>{selectedSubscription.slug}</strong>.
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Raison de l'annulation</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={cancelForm.reason}
                      onChange={(e) => setCancelForm({...cancelForm, reason: e.target.value})}
                      placeholder="Expliquez la raison de l'annulation..."
                      maxLength="500"
                    />
                    <div className="form-text">{cancelForm.reason.length}/500 caractères</div>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="immediate"
                      checked={cancelForm.immediate}
                      onChange={(e) => setCancelForm({...cancelForm, immediate: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="immediate">
                      Annulation immédiate (sinon à la fin de la période)
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCancelModal(false);
                      setCancelForm({ reason: '', immediate: false });
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-danger">
                    <i className="bi bi-x-circle me-1"></i>
                    Confirmer l'Annulation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestion des Classes */}
      {showClassesModal && selectedSubscription && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060}}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-collection-play me-2"></i>
                  Gestion des Classes - {selectedSubscription.slug}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowClassesModal(false);
                    // Rafraîchir les détails de l'abonnement si nécessaire
                    if (showDetailsModal) {
                      handleShowDetails(selectedSubscription);
                    }
                  }}
                ></button>
              </div>
              <div className="modal-body p-4">
                <SubscriptionClasses 
                  subscription={selectedSubscription}
                  onClose={() => {
                    setShowClassesModal(false);
                    // Rafraîchir les détails de l'abonnement si nécessaire
                    if (showDetailsModal) {
                      handleShowDetails(selectedSubscription);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSubscriptions;
